import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PlusIcon from 'icons/Plus.icon';
import TrashIcon from 'icons/Trash.icon';
import useCallbackAsync from 'hooks/useCallbackAsync';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { DocumentPublishedEventProps } from 'models/MixpanelEvents';
import { getIsOwner } from 'utils';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { MessageFileHandlers } from '../components/MessageFile/MessageFile.component';

type UseChatFileMenuOptionsProps = {
  id: string;
  name: string;
  type: string;
  disabled?: boolean;
  isDataRoomFile?: boolean;
  canMoveFileToDataRoom?: boolean;
  canRemoveFile?: boolean;
  canRemoveFileFromDataRoom?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId?: string;
  userSide?: QAndASides;
  /** run after the file leaves the chat, on top of the shared list update */
  onRemoved?: (fileId: string) => void;
  /** run after the file lands in the Data Room, on top of the shared list update */
  onSavedToDataRoom?: (fileId: string) => void;
} & Pick<
  MessageFileHandlers,
  'onFileAddToDataRoom' | 'onFileRemove' | 'onFileRemoveFromDataRoom'
>;

/**
 * The "save to Data Room" / "remove file" menu behind a chat file, shared by the message
 * attachment rows and the "Files in chat" section so both confirm and report the same way.
 */
export default function useChatFileMenuOptions(
  props: UseChatFileMenuOptionsProps,
) {
  const {
    id,
    name,
    type,
    disabled,
    isDataRoomFile,
    canMoveFileToDataRoom,
    canRemoveFile,
    canRemoveFileFromDataRoom,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    onFileAddToDataRoom,
    onFileRemove,
    onFileRemoveFromDataRoom,
    onRemoved,
    onSavedToDataRoom,
  } = props;

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { popSuccess } = useContext(PopNotificationsContext);
  const {
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const isOwner = useMemo(
    () => getIsOwner(ownerId, currentUserId),
    [currentUserId, ownerId],
  );

  const [removeFile, loadingRemove] = useCallbackAsync(async () => {
    const remove = async (fromDataRoom: boolean) => {
      await onFileRemove(id, fromDataRoom);
      onRemoved?.(id);
      popSuccess(t('fileSuccessfullyRemoved'));
    };

    if (!isDataRoomFile) {
      await remove(false);
      return;
    }

    if (
      await confirm(
        {
          title: t('removingFromDataRoom'),
          content: t('wouldYouLikeToRemoveFileFromDR'),
          type: 'delete',
          onClose: async () => await remove(false),
        },
        true,
      )
    )
      await remove(true);
  });

  const [removeFileFromDataRoom, loadingRemoveFromDataRoom] = useCallbackAsync(
    async () => {
      if (
        await confirm({
          type: 'delete',
          title: t('deleteDocumentTitle'),
          content: t('deleteDocumentDescription', { documentName: name }),
          submitBtnText: t('remove'),
          cancelBtnText: t('cancel'),
        })
      ) {
        await onFileRemoveFromDataRoom(id);
        popSuccess(t('fileSuccessfullyRemovedFromDR'));
      }
    },
  );

  const [saveFileToDataRoom, loadingAdd] = useCallbackAsync(async () => {
    mixpanelService.documentPublished(
      getMixpanelProps({
        dataRoomId,
        dataRoomName,
        documentName: name,
        documentType: type,
        isOwner,
        userSide,
      }) as DocumentPublishedEventProps,
    );

    await onFileAddToDataRoom(id);
    onSavedToDataRoom?.(id);
  });

  const isDisabled =
    disabled || loadingAdd || loadingRemove || loadingRemoveFromDataRoom;

  const options = useMemo(
    () => [
      ...(!isDataRoomFile && canMoveFileToDataRoom && onFileAddToDataRoom
        ? [
            {
              label: t('saveToDR'),
              disabled: isDisabled,
              icon: <PlusIcon />,
              onClick: saveFileToDataRoom,
            },
          ]
        : []),
      ...(isDataRoomFile &&
      canRemoveFileFromDataRoom &&
      onFileRemoveFromDataRoom
        ? [
            {
              label: t('removeFromDR'),
              disabled: isDisabled,
              alert: true,
              icon: <TrashIcon />,
              onClick: removeFileFromDataRoom,
            },
          ]
        : []),
      ...(canRemoveFile && onFileRemove
        ? [
            {
              label: t('removeFile'),
              disabled: isDisabled,
              alert: true,
              icon: <TrashIcon />,
              onClick: removeFile,
            },
          ]
        : []),
    ],
    [
      isDisabled,
      isDataRoomFile,
      canMoveFileToDataRoom,
      canRemoveFile,
      canRemoveFileFromDataRoom,
      t,
      saveFileToDataRoom,
      onFileAddToDataRoom,
      onFileRemove,
      onFileRemoveFromDataRoom,
      removeFile,
      removeFileFromDataRoom,
    ],
  );

  return { options, isDisabled };
}
